import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Map } from 'immutable';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import useMediaQuery from '@mui/material/useMediaQuery';

import AlarmsToTriage, { ALARM_SEVERITIES, type AlarmsToTriageSegment } from 'src/screen/v2/Protected/DashboardScreen/AlarmsToTriage';
import AmbientTemperaturePanel from 'src/screen/v2/Protected/DashboardScreen/AmbientTemperaturePanel';
import AreaFilter from 'src/component/v2/AreaFilter';
import DeviceSummaryBar from 'src/screen/v2/Protected/DashboardScreen/DeviceSummaryBar';
import FacilityMap from 'src/screen/v2/Protected/DashboardScreen/FacilityMap';
import TriagedAlarms from 'src/screen/v2/Protected/DashboardScreen/TriagedAlarms';
import { fetchTriageSummary, fetchTriagedSummary } from 'src/module/alarm/action';
import PotentialEnergySavings, { type EnergySeriesPoint } from 'src/screen/v2/Protected/DashboardScreen/PotentialEnergySavings';
import { DEFAULT_ENERGY_PERIOD, type EnergyPeriod } from 'src/screen/v2/Protected/DashboardScreen/PotentialEnergySavings/period';
import { fetchEnergySavings, fetchFacilitySummary, fetchSensorHistory } from 'src/module/device/action';
import { getClientId, getCriticalityLabels } from 'src/module/authentication/selector';
import { getDevicesSelectedFiltersObj } from 'src/module/filter/selector';
import {
  getEnergySavings,
  getEnergySavingsError,
  getEnergySavingsLoading,
  getFacilitySummary,
  getSensorData
} from 'src/module/device/selector';
import { getTriagedSummary, getTriagedSummaryLoading, getTriageSummary, getTriageSummaryLoading } from 'src/module/alarm/selector';
import { isWhitelabel } from 'src/utils/utils';
import { pushHistory } from 'src/module/navigation/action';
import { setSelectedAttribute } from 'src/module/filter/action';
import { useTranslation } from 'src/i18n';
import { DS_SCREENS } from 'src/style/dsScreens';


const DEVICE_LIST_PATH = '/devices/list';

const ENERGY_ROLLUP = 'Monthly';
/* The map's height scales with its width, so below this it's too short to hold
 * the weather card without clipping it. */
const WEATHER_OVERLAY_QUERY = `(min-width: ${DS_SCREENS['ds-map-overlay']})`;

export default function DashboardScreen () {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const title = isWhitelabel() ? 'ATCOM' : 'SmartTrace';

  const selectedFilters = useSelector(getDevicesSelectedFiltersObj, isEqual);
  const clientId = useSelector(getClientId);
  const criticalityLabels = useSelector(getCriticalityLabels);
  const summary = useSelector(getFacilitySummary);
  const triageSummary = useSelector(getTriageSummary);
  const triagedSummary = useSelector(getTriagedSummary);

  /* The triage card tracks the fetch itself rather than an empty store: the
   * alarm slice is persisted, so on a warm load it shows the last session's
   * counts until the refetch dispatches. */
  const showTriageSkeletons = useSelector(getTriageSummaryLoading);
  const showTriagedSkeletons = useSelector(getTriagedSummaryLoading);

  const [energyPeriod, setEnergyPeriod] = useState<EnergyPeriod>(DEFAULT_ENERGY_PERIOD);

  const energySavings = useSelector(getEnergySavings);
  const energySavingsLoading = useSelector(getEnergySavingsLoading);
  const energySavingsError = useSelector(getEnergySavingsError);

  const sensorDataSelector = useMemo(
    () => getSensorData(energyPeriod, ENERGY_ROLLUP),
    [energyPeriod]
  );
  const sensorData = useSelector(sensorDataSelector);
  const overlayWeather = useMediaQuery(WEATHER_OVERLAY_QUERY);

  /*
   * The pills set the attribute filter on the way out to the device list, so
   * coming back would leave it applied to a screen with no chip to show or
   * clear it -- and every future v2 component here would inherit it. The area
   * and unit filters are deliberately left alone; those persist by design.
   */
  useEffect(() => {
    dispatch(setSelectedAttribute(null));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchFacilitySummary(Map(selectedFilters)));
  }, [dispatch, clientId, selectedFilters]);

  useEffect(() => {
    dispatch(fetchTriageSummary(Map(selectedFilters)));
  }, [dispatch, clientId, selectedFilters]);

  useEffect(() => {
    dispatch(fetchTriagedSummary(Map(selectedFilters)));
  }, [dispatch, clientId, selectedFilters]);
  const energyStartDate = useMemo(() => {
    const [count, unit] = energyPeriod.split('-');
    return moment.utc().subtract(Number(count), unit as moment.unitOfTime.DurationConstructor).format('YYYY-MM-DD');
  }, [energyPeriod]);

  useEffect(() => {
    dispatch(fetchEnergySavings(Map(selectedFilters), energyStartDate, null));
  }, [dispatch, clientId, selectedFilters, energyStartDate]);

  useEffect(() => {
    dispatch(fetchSensorHistory(Map(selectedFilters), energyPeriod, ENERGY_ROLLUP, null, null));
  }, [dispatch, clientId, selectedFilters, energyPeriod]);

  /*
   * Drill in to the devices matching a pill. Both filters ride in redux: the
   * attribute is set here, and the area/unit filters are already there and are
   * not cleared on navigation, so the list opens scoped to both.
   */
  const openDeviceList = useCallback((attribute: string) => () => {
    dispatch(setSelectedAttribute(attribute));
    dispatch(pushHistory(DEVICE_LIST_PATH));
  }, [dispatch]);

  /*
   * The card shows three severity slots, but criticality is a per-client scale
   * of any depth (`preferences.criticalityLabels`). Take the three most severe
   * levels above 0 -- 0 means "not critical" and isn't triage-worthy -- and
   * label each with the client's own wording.
   */
  const segments: AlarmsToTriageSegment[] = useMemo(() => {
    const byCriticality = triageSummary.get('byCriticality', Map()) || Map();

    return ALARM_SEVERITIES.map((severity, idx) => {
      const criticality = criticalityLabels.size - 1 - idx;
      if (criticality < 1) return null;

      return {
        severity,
        count: byCriticality.get(String(criticality), 0) || 0,
        label: criticalityLabels.get(criticality) || `${criticality}`,
      };
    }).filter(Boolean) as AlarmsToTriageSegment[];
  }, [triageSummary, criticalityLabels]);

  const workOrderGroups: number[] = useMemo(
    () => (triagedSummary.get('workOrderGroups')?.toJS?.() ?? []),
    [triagedSummary]
  );
  const energySeries: EnergySeriesPoint[] = useMemo(() => {
    const times = sensorData.get('time');
    if (!times) return [];

    const kwh = sensorData.get('kwh');
    const weather = sensorData.get('weather');

    /* Null stays null: a bucket with no readings sums to null in Postgres, and drawing
     * that as a 0 kWh bar would claim the facility drew nothing. The chart gaps it. */
    const value = (v: any) => (v == null ? null : Number(v));

    return times.toJS().map((time: string, idx: number) => ({
      label: moment.utc(time).format('MMM'),
      kwh: value(kwh?.get(idx)),
      ambient: value(weather?.get(idx)),
    }));
  }, [sensorData]);

  return (
    <>
      <Helmet>
        <title>{title} - {t('dashboard.title')}</title>
      </Helmet>

      {/* The design has no visible page title, but the card headings below
        * need an <h1> above them to hang off. */}
      <h1 className='ds-sr-only'>{t('dashboard.title')}</h1>

      <div className='ds-relative'>
        <div className='ds-absolute ds-left-0 ds-right-0 ds-top-0 ds-z-10 ds-p-3.5'>
          <AreaFilter />
        </div>

        <FacilityMap
          topRight={overlayWeather ? <AmbientTemperaturePanel /> : null}
          bottomLeft={
            <DeviceSummaryBar
              loto={summary.get('loto')}
              offline={summary.get('offline')}
              alarming={summary.get('alarming')}
              totalDevices={summary.get('totalDevices')}
              forcedOn={summary.get('forcedOn')}
              onLotoClick={openDeviceList('loto')}
              onOfflineClick={openDeviceList('offline')}
              onAlarmingClick={openDeviceList('alarming')}
              onForcedOnClick={openDeviceList('forcedOn')}
              loading={summary.isEmpty()}
            />
          }
        />
      </div>

      {/* The map is full-bleed; everything below it is inset, per Figma. */}
      <div className='ds-flex ds-flex-col ds-gap-4 ds-p-4'>
        {overlayWeather ? null : <AmbientTemperaturePanel />}

        <AlarmsToTriage
          total={triageSummary.get('total', 0)}
          acknowledged={triageSummary.get('acknowledged', 0)}
          segments={segments}
          loading={showTriageSkeletons}
        />

        <div className='ds-grid ds-grid-cols-1 ds-gap-4 ds-pair:ds-grid-cols-2'>
          <TriagedAlarms
            total={triagedSummary.get('total', 0)}
            resolvedThisWeek={triagedSummary.get('resolvedThisWeek', 0)}
            withWorkOrders={triagedSummary.get('withWorkOrders', 0)}
            closedThisWeek={triagedSummary.get('closedThisWeek', 0)}
            shelved={triagedSummary.get('shelved', 0)}
            expiringSoon={triagedSummary.get('expiringSoon', 0)}
            workOrderGroups={workOrderGroups}
            loading={showTriagedSkeletons}
          />
          <PotentialEnergySavings
            savings={energySavings.get('savings', 0)}
            currency={energySavings.get('currency', 'USD')}
            ratePerKwh={energySavings.get('ratePerKwh')}
            rateIsDefault={energySavings.get('rateIsDefault', true)}
            wastedKwh={energySavings.get('wastedKwh', 0)}
            totalKwh={energySavings.get('totalKwh', 0)}
            series={energySeries}
            period={energyPeriod}
            onPeriodChange={setEnergyPeriod}
            loading={energySavingsLoading}
            error={energySavingsError}
          />
        </div>
      </div>
    </>
  );
}
